import { useMemo, useRef, useState } from 'react';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { BlockButton } from '@/components/BlockButton';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import useFetchActiveUsers from '../../api/useFetchActiveUsers';
import useBulkChangeOrganizationUser from '../api/useBulkChangeOrganizationUser';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  ids: number[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export default function BulkChangeOrganizationUserModal({
  ids,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, () => setIsMenuOpen(false));
  const { data: activeUsers } = useFetchActiveUsers();
  const { mutateAsync: bulkChange, isPending } = useBulkChangeOrganizationUser();

  const users = useMemo(() => activeUsers?.records ?? [], [activeUsers]);
  const selectedUsers = users.filter((user) => selectedUserIds.includes(user.id));
  const additionalCount = selectedUsers.length - 1;

  const handleToggle = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0) return;
    try {
      await bulkChange({ ids, organization_user_ids: selectedUserIds });
      showSuccessToast('担当者を変更しました');
      setSelectedUserIds([]);
      onSuccess();
    } catch {
      showErrorToast('担当者の変更に失敗しました');
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setIsMenuOpen(false);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[728px]">
        <div className="flex h-[53px] items-center gap-1 border-b border-solid border-border px-6">
          <p className="text-sm font-bold">担当者を一括変更</p>
        </div>
        <div className="border-b border-solid border-border p-6">
          <p className="mb-4 text-base">新しい担当者を選択してください（複数選択可）</p>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className={clsx(
                'flex w-full cursor-pointer items-center justify-between rounded border border-solid border-border bg-white px-3 py-2 text-sm',
                isMenuOpen && 'ring-2 ring-primary'
              )}
              onClick={() => setIsMenuOpen((prev) => !prev)}
            >
              <span className={clsx(selectedUsers.length === 0 && 'text-placeholder')}>
                {selectedUsers.length === 0 ? (
                  '担当者を選択'
                ) : additionalCount > 0 ? (
                  <>
                    {selectedUsers[0].name} <span className="text-primary">+{additionalCount}</span>
                  </>
                ) : (
                  selectedUsers[0].name
                )}
              </span>
              <motion.svg
                width={20}
                height={20}
                viewBox="0 0 20 20"
                fill="none"
                animate={{ rotate: isMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.95 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute top-full left-0 z-50 mt-1 max-h-[240px] w-full overflow-y-auto rounded border border-solid border-border bg-white shadow-lg"
                >
                  {users.map((user) => {
                    const isSelected = selectedUserIds.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        className={clsx(
                          'flex cursor-pointer items-center px-3 py-2 text-sm transition-colors duration-100 hover:bg-gray',
                          isSelected && 'bg-gray'
                        )}
                        onClick={() => handleToggle(user.id)}
                      >
                        <span>{user.name}</span>
                        <AnimatePresence>
                          {isSelected && (
                            <motion.img
                              initial={{ opacity: 0, scale: 0.5 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.5 }}
                              transition={{ duration: 0.15 }}
                              src="/agent/candidates/icon-check-circle.svg"
                              width={16}
                              height={16}
                              alt=""
                              className="ml-auto"
                            />
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 px-6">
          <div className="w-[94px]">
            <BlockButton type="button" colorType="white" text="キャンセル" onClick={handleClose} />
          </div>
          <div className="w-[108px]">
            <BlockButton
              type="button"
              colorType="blue"
              text="変更する"
              onClick={handleSubmit}
              disabled={selectedUserIds.length === 0 || isPending}
            />
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
